import { ControlledTextField } from '@if/mui'
import { Done as DoneIcon } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import LinkIcon from '@mui/icons-material/Link'
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Link } from '@mui/material'
import AsyncActionButton from '@src/shared/components/AsyncActionButton'
import { AddTicketToHistoryDto, useAddTicketToHistory } from '@src/tools/error-tracker/api/exceptions-api'
import { reportErrorToConsole } from '@src/shared/services/error-logger'
import { EExceptionStatus } from '@src/tools/error-tracker/types/Constants'
import { ExceptionLog } from '@src/tools/error-tracker/types/ExceptionLog'
import { memo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface props {
  exceptionLog: ExceptionLog | null
  open: boolean
  onSuccess: () => void
  onCancel: () => void
}

const AddTicketDialog = ({ exceptionLog, open, onSuccess, onCancel }: props) => {
  const { t } = useTranslation()
  const DevopsBaseUrl = 'https://dev.azure.com/IcebergFinance/IcebergFinance/_workitems/edit/'

  const [addTicketToHistory, isPending, isError, resetMutation] = useAddTicketToHistory()

  const { control, getValues, handleSubmit, reset } = useForm<AddTicketToHistoryDto>({
    defaultValues: {
      devopsTicketNumber: '',
      status: EExceptionStatus.TicketAssigned,
    },
  })

  useEffect(() => {
    reset({
      devopsTicketNumber: '',
      status: EExceptionStatus.TicketAssigned,
    })
    resetMutation()
  }, [exceptionLog, open, reset, resetMutation])

  const handleFormSubmit = async (formData: AddTicketToHistoryDto) => {
    if (exceptionLog?.id) {
      try {
        await addTicketToHistory({ id: exceptionLog.id, dto: formData })
        onSuccess()
      } catch (err) {
        // Error is handled by react-query and shown via isError
        reportErrorToConsole(err)
      }
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="add-ticket-dialog-title"
      aria-describedby="add-ticket-dialog-description"
      maxWidth="md"
      keepMounted
      transitionDuration={0}
      disablePortal
    >
      <DialogTitle style={{ borderBottom: '1px solid' }} id="add-ticket-dialog-title" sx={{ textAlign: 'center' }}>
        {t('exceptionsColumns.addNewTicket')}
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{
            position: 'absolute',
            right: 15,
            top: 15,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Grid container direction="column" spacing={2} sx={{ mt: 5, width: 600 }}>
            {isError && (
              <Grid>
                <Alert severity="error">{t('common.errorOccurred')}</Alert>
              </Grid>
            )}
            <Grid>
              <ControlledTextField
                name="devopsTicketNumber"
                control={control}
                focused
                required
                disabled={isPending}
                placeholder={t('exceptionsColumns.devOpsTicket')}
                label={t('exceptionsColumns.devOpsTicket')}
                type="number"
                slotProps={{ htmlInput: { maxLength: 5 } }}
              />
              {getValues('devopsTicketNumber') && (
                <Link href={DevopsBaseUrl + getValues('devopsTicketNumber')} target="_blank">
                  <LinkIcon style={{ marginTop: 8 }} fontSize="large" />
                </Link>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <AsyncActionButton
            variant="contained"
            type="submit"
            startIcon={<DoneIcon />}
            disabled={isPending}
            isPending={isPending}
          >
            {t('common.apply')}
          </AsyncActionButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}
export default memo(AddTicketDialog)